import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronRight, Clock3, Copy, Download, Eye, LogIn, Mail, Share2, Users, X } from 'lucide-react';
import { admin } from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const dateInput = date => date.toISOString().slice(0, 10);
const initialEnd = dateInput(new Date());
const initialStart = dateInput(new Date(Date.now() - (30 * 86400000)));

const saveBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const formatDuration = seconds => {
  if (!seconds) return '0m';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatDateTime = value => {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Date unavailable';
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const humanizeActivity = value => ({
  download: 'Downloaded',
  email: 'Emailed',
  share: 'Shared',
  social_publish: 'Published to social media',
  flyer_view: 'Viewed flyer',
  page_time: 'Page visit',
  login: 'Logged in'
}[value] || String(value || 'Activity').replaceAll('_', ' '));

const DrilldownDialog = ({ selection, loading, error, onClose }) => {
  const data = selection?.data;
  const records = data?.records || [];
  const summary = data?.metric === 'page_time'
    ? `${formatDuration(data.averageDurationSeconds)} average across ${data.total || 0} tracked visit${data.total === 1 ? '' : 's'}`
    : `${data?.total || 0} matching activit${data?.total === 1 ? 'y' : 'ies'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" role="presentation">
      <button type="button" className="absolute inset-0 bg-gray-950/45" aria-label="Close analytics details" onClick={onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="analytics-drilldown-title"
        className="relative flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-white shadow-2xl sm:max-w-3xl sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 sm:px-6">
          <div>
            <h2 id="analytics-drilldown-title" className="text-lg font-bold text-gray-900">{selection.title}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {loading ? 'Loading activity…' : error ? 'Details unavailable' : summary}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800" aria-label="Close analytics details">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4 sm:px-6">
          {loading ? (
            <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500/30 border-t-primary-500" /></div>
          ) : error ? (
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-8 text-center text-sm text-red-700">{error}</div>
          ) : records.length ? (
            <div className="space-y-2">
              {records.map(record => {
                const primaryName = record.contactName || record.email || 'Unnamed user';
                const secondary = [
                  record.companyName && record.companyName !== primaryName ? record.companyName : null,
                  record.email && record.email !== primaryName ? record.email : null
                ].filter(Boolean).join(' • ');
                const context = [record.contentTitle, record.formatName].filter(Boolean).join(' • ');
                return (
                  <Link
                    key={record.id}
                    to={`/admin/users?user=${encodeURIComponent(record.userId)}`}
                    onClick={onClose}
                    className="group flex min-h-20 items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 transition hover:border-primary-200 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    aria-label={`Open ${primaryName} account activity`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-900">{primaryName}</p>
                      {secondary && <p className="truncate text-sm text-gray-500">{secondary}</p>}
                      <p className="mt-1 text-xs text-gray-500">
                        <span className="font-medium text-gray-700">{humanizeActivity(record.actionType)}</span>
                        {record.durationSeconds ? ` • ${formatDuration(record.durationSeconds)}` : ''}
                        {context ? ` • ${context}` : ''}
                        {record.pagePath ? ` • ${record.pagePath}` : ''}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">{formatDateTime(record.occurredAt)}</p>
                    </div>
                    <ChevronRight size={19} className="flex-shrink-0 text-gray-300 transition group-hover:text-primary-500" />
                  </Link>
                );
              })}
              {data.total > records.length && (
                <p className="pt-2 text-center text-xs text-gray-500">Showing the 100 most recent activities in this period.</p>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">No matching activity in this period.</div>
          )}
        </div>
      </section>
    </div>
  );
};

const RankedList = ({ title, subtitle, items }) => (
  <section className="card p-5">
    <h2 className="font-semibold text-gray-900">{title}</h2>
    <p className="mb-4 text-xs text-gray-500">{subtitle}</p>
    {items?.length ? <div className="space-y-2">{items.slice(0, 6).map((item, index) => (
      <Link key={item.id} to={`/flyers/${item.id}`} className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5 transition hover:bg-primary-50">
        <span className="w-6 text-center text-xs font-bold text-primary-600">{index + 1}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{item.title}</span>
        <span className="text-sm font-semibold text-gray-700">{item.count}</span>
      </Link>
    ))}</div> : <p className="py-7 text-center text-sm text-gray-400">No activity in this period</p>}
  </section>
);

const AdminAnalytics = () => {
  const { isSuperAdmin } = useAuth();
  const [scope, setScope] = useState(isSuperAdmin ? 'all' : 'mine');
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState('');
  const drilldownRequest = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const response = await admin.getAnalytics({ scope, startDate, endDate }, { signal: controller.signal });
        setAnalytics(response.data);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('Analytics error:', error);
          toast.error(error.response?.data?.error || 'Failed to load analytics');
        }
      } finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(load, 200);
    return () => { clearTimeout(debounce); controller.abort(); };
  }, [scope, startDate, endDate]);

  useEffect(() => {
    if (!drilldown) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === 'Escape') setDrilldown(null);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [drilldown]);

  const openDrilldown = async ({ metric, title, formatId, actionType }) => {
    const requestId = drilldownRequest.current + 1;
    drilldownRequest.current = requestId;
    const fallbackTitles = {
      logins: 'Login activity',
      views: 'Flyer views',
      exports: 'All exports',
      shares: 'Shares',
      emails: 'Emails',
      page_time: 'Page-time activity'
    };
    setDrilldown({ metric, title: title || fallbackTitles[metric] || 'Analytics details', formatId, actionType, data: null });
    setDrilldownLoading(true);
    setDrilldownError('');
    try {
      const response = await admin.getAnalyticsDrilldown({
        scope,
        startDate,
        endDate,
        metric,
        ...(formatId ? { formatId } : {}),
        ...(actionType ? { actionType } : {})
      });
      if (drilldownRequest.current === requestId) {
        setDrilldown(current => current ? {
          ...current,
          title: current.title || response.data.title,
          data: response.data
        } : current);
      }
    } catch (error) {
      if (drilldownRequest.current === requestId) {
        setDrilldownError(error.response?.data?.error || 'Failed to load analytics details');
      }
    } finally {
      if (drilldownRequest.current === requestId) setDrilldownLoading(false);
    }
  };

  const closeDrilldown = () => {
    drilldownRequest.current += 1;
    setDrilldown(null);
    setDrilldownError('');
  };

  const exportActivity = async () => {
    try {
      const response = await admin.exportActivity({ scope, startDate, endDate });
      saveBlob(response.data, `activity-${scope}-${startDate}-to-${endDate}.csv`);
      toast.success('Activity report downloaded');
    } catch (error) {
      toast.error('Failed to export activity');
    }
  };

  const exportUsers = async () => {
    try {
      const response = await admin.exportUsers({ scope });
      saveBlob(response.data, `users-${scope}-${Date.now()}.csv`);
      toast.success('User report downloaded');
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  const maxFormatCount = useMemo(() => Math.max(1, ...(analytics?.formats || []).map(item => item.count)), [analytics]);
  const metrics = analytics?.metrics || {};
  const cards = [
    { metric: 'logins', label: 'Logins', value: metrics.logins || 0, note: `${metrics.uniqueLoginUsers || 0} unique users`, description: 'Every recorded sign-in during the selected dates.', icon: LogIn, tone: 'bg-green-100 text-green-600' },
    { metric: 'views', label: 'Flyer views', value: metrics.views || 0, note: 'Content detail views', description: 'Every time a user opened a flyer detail page.', icon: Eye, tone: 'bg-blue-100 text-blue-600' },
    { metric: 'exports', label: 'Total exports', value: metrics.exports || 0, note: `${metrics.downloads || 0} downloads`, description: 'Downloads, emails, shares, and direct social publishing actions.', icon: Download, tone: 'bg-primary-100 text-primary-600' },
    { metric: 'shares', label: 'Shares', value: metrics.shares || 0, note: `${metrics.captionCopies || 0} caption copies`, description: 'Native share actions and direct social publishing.', icon: Share2, tone: 'bg-purple-100 text-purple-600' },
    { metric: 'emails', label: 'Emails', value: metrics.emails || 0, note: 'Flyers emailed to users', description: 'Flyers sent through the Marketing Hub email workflow.', icon: Mail, tone: 'bg-amber-100 text-amber-600' },
    { metric: 'page_time', label: 'Avg. page time', value: formatDuration(metrics.averagePageSeconds), note: `${formatDuration(metrics.totalPageSeconds)} total`, description: 'Average duration of tracked page visits during the selected dates.', icon: Clock3, tone: 'bg-cyan-100 text-cyan-600' }
  ];

  return (
    <div className="animate-fade-in">
      <div className="mb-7">
        <h1 className="font-display text-3xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-2 text-gray-600">Detailed usage trends, formats, and content performance</p>
      </div>

      <div className="card mb-6 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <label className="flex-1 text-sm font-medium text-gray-700">User scope
            <select value={scope} onChange={event => setScope(event.target.value)} className="input-field mt-1.5">
              <option value="mine">My assigned users</option>{isSuperAdmin && <option value="all">All users</option>}
            </select>
          </label>
          <label className="flex-1 text-sm font-medium text-gray-700">Start date<input type="date" value={startDate} max={endDate} onChange={event => setStartDate(event.target.value)} className="input-field mt-1.5" /></label>
          <label className="flex-1 text-sm font-medium text-gray-700">End date<input type="date" value={endDate} min={startDate} max={initialEnd} onChange={event => setEndDate(event.target.value)} className="input-field mt-1.5" /></label>
          <button type="button" onClick={exportActivity} className="btn-primary inline-flex items-center justify-center gap-2"><Download size={17} /> Export activity</button>
          <button type="button" onClick={exportUsers} className="btn-outline inline-flex items-center justify-center gap-2"><Users size={17} /> Export users</button>
        </div>
      </div>

      {loading ? <div className="flex h-52 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500/30 border-t-primary-500" /></div> : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">{cards.map(card => { const Icon = card.icon; return (
            <button
              type="button"
              key={card.label}
              onClick={() => openDrilldown(card)}
              title={card.description}
              aria-label={`${card.label}: ${card.value}. View details`}
              className="card group p-5 text-left transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <div className="flex items-start justify-between gap-3"><div><p className="text-sm text-gray-500">{card.label}</p><p className="mt-1 text-3xl font-bold text-gray-900">{card.value}</p><p className="mt-1 text-xs text-gray-500">{card.note}</p></div><div className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.tone}`}><Icon size={22} /></div></div>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">View details <ChevronRight size={14} className="transition group-hover:translate-x-0.5" /></span>
            </button>
          ); })}</div>

          <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <section className="card p-5"><div className="mb-4 flex items-center gap-2"><BarChart3 size={19} className="text-primary-600" /><div><h2 className="font-semibold text-gray-900">Format popularity</h2><p className="text-xs text-gray-500">Which output sizes users choose</p></div></div>
              {analytics?.formats?.length ? <div className="space-y-4">{analytics.formats.map(item => (
                <button type="button" key={item.id} onClick={() => openDrilldown({ metric: 'format', title: item.name, formatId: item.id })} className="group block w-full rounded-lg p-1 text-left transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400" aria-label={`View activity for ${item.name}`}><div className="mb-1.5 flex justify-between text-sm"><span className="font-medium text-gray-700">{item.name}</span><span className="flex items-center gap-1 text-gray-500">{item.count}<ChevronRight size={14} className="text-gray-300 group-hover:text-primary-500" /></span></div><div className="h-2.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.max(4, (item.count / maxFormatCount) * 100)}%` }} /></div></button>
              ))}</div> : <p className="py-8 text-center text-sm text-gray-400">No format data yet</p>}
            </section>

            <section className="card p-5"><div className="mb-4 flex items-center gap-2"><Copy size={19} className="text-primary-600" /><div><h2 className="font-semibold text-gray-900">Export mix</h2><p className="text-xs text-gray-500">How users take content out of the hub</p></div></div>
              {analytics?.actionBreakdown?.length ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{analytics.actionBreakdown.map(item => (
                <button type="button" key={item.actionType} onClick={() => openDrilldown({ metric: 'action', title: `${humanizeActivity(item.actionType)} activity`, actionType: item.actionType })} className="group rounded-xl bg-gray-50 p-4 text-center transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400" aria-label={`View ${item.actionType.replaceAll('_', ' ')} activity`}><p className="text-2xl font-bold text-gray-900">{item.count}</p><p className="mt-1 inline-flex items-center gap-1 text-xs font-medium capitalize text-gray-500">{item.actionType.replaceAll('_', ' ')}<ChevronRight size={13} className="text-gray-300 group-hover:text-primary-500" /></p></button>
              ))}</div> : <p className="py-8 text-center text-sm text-gray-400">No exports yet</p>}
            </section>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <RankedList title="Most viewed content" subtitle="Flyers users opened" items={analytics?.topViewed} />
            <RankedList title="Most downloaded content" subtitle="Files saved directly" items={analytics?.topDownloaded} />
            <RankedList title="Most shared content" subtitle="Branded images prepared for sharing" items={analytics?.topShared} />
            <RankedList title="Most emailed content" subtitle="Flyers delivered by email" items={analytics?.topEmailed} />
          </div>
        </>
      )}
      {drilldown && (
        <DrilldownDialog
          selection={drilldown}
          loading={drilldownLoading}
          error={drilldownError}
          onClose={closeDrilldown}
        />
      )}
    </div>
  );
};

export default AdminAnalytics;
